import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../api/endpoints";
import { errorMessage } from "../api/client";
import type { Camera } from "../api/types";
import { useToast } from "../components/Toast";

const POLL_MS = 3000;
const TIMEOUT_MS = 45_000;

/**
 * Ask a camera for a fresh picture, then poll /api/cameras until its
 * thumbnail_at changes (battery cameras take 5 to 15 s) or 45 s pass.
 * `onCameras` receives every fresh list so the caller can update its copy.
 */
export function useSnapshot(onCameras: (list: Camera[]) => void) {
  const toast = useToast();
  const [snapping, setSnapping] = useState<Record<string, boolean>>({});
  const pollers = useRef(new Set<ReturnType<typeof setInterval>>());
  const cb = useRef(onCameras);
  cb.current = onCameras;

  useEffect(() => {
    const set = pollers.current;
    return () => set.forEach((t) => clearInterval(t));
  }, []);

  const snapshot = useCallback(
    async (cam: Camera) => {
      setSnapping((s) => ({ ...s, [cam.id]: true }));
      const before = cam.thumbnail_at;
      try {
        await api.snapshot(cam.id);
      } catch (err) {
        setSnapping((s) => ({ ...s, [cam.id]: false }));
        toast.show(`Snapshot failed for ${cam.name}. ${errorMessage(err)}`, "error");
        return;
      }
      const started = Date.now();
      const timer = setInterval(async () => {
        try {
          const list = await api.cameras();
          cb.current(list);
          const now = list.find((c) => c.id === cam.id);
          if (now && now.thumbnail_at !== before) {
            clearInterval(timer);
            pollers.current.delete(timer);
            setSnapping((s) => ({ ...s, [cam.id]: false }));
            toast.show(`New picture from ${cam.name}.`);
            return;
          }
        } catch {
          // Keep polling until the timeout.
        }
        if (Date.now() - started > TIMEOUT_MS) {
          clearInterval(timer);
          pollers.current.delete(timer);
          setSnapping((s) => ({ ...s, [cam.id]: false }));
          toast.show(`${cam.name} has not sent a new picture yet. Battery cameras can be slow; it will appear on the next refresh.`, "error");
        }
      }, POLL_MS);
      pollers.current.add(timer);
    },
    [toast],
  );

  return { snapping, snapshot };
}
